/**
 * Date Calculation Engine
 *
 * Centralizes all date calculation logic across the bond calculator system.
 * Provides efficient date operations while avoiding timezone issues.
 *
 * Design Philosophy:
 * - Work with YYYY-MM-DD strings to avoid timezone complications
 * - Provide both calculation methods and cached lookups where appropriate
 * - Support 30/360 day count convention for financial calculations
 * - Reuse existing date-utils functions where possible
 */

import { addYears, compareDates, getYear, isValidDateString } from '~/utils/date-utils';

export class DateCalculationEngine {
  /**
   * Calculate the number of days between two dates using actual calendar days
   * @param startDate Starting date in YYYY-MM-DD format
   * @param endDate Ending date in YYYY-MM-DD format
   * @returns Number of days between dates (can be negative if endDate < startDate)
   */
  static daysBetween(startDate: string, endDate: string): number {
    if (!isValidDateString(startDate) || !isValidDateString(endDate)) {
      throw new Error('Invalid date strings for daysBetween calculation');
    }

    const start = new Date(`${startDate}T00:00:00Z`);
    const end = new Date(`${endDate}T00:00:00Z`);
    const diffInMs = end.getTime() - start.getTime();
    return Math.floor(diffInMs / (1000 * 60 * 60 * 24));
  }

  /**
   * Calculate the number of days between two dates using 30/360 day count convention
   * This is the standard for bond calculations
   * @param startDate Starting date in YYYY-MM-DD format
   * @param endDate Ending date in YYYY-MM-DD format
   * @returns Number of days using 30/360 convention
   */
  static days360Between(startDate: string, endDate: string): number {
    if (!isValidDateString(startDate) || !isValidDateString(endDate)) {
      throw new Error('Invalid date strings for days360Between calculation');
    }

    const [y1, m1, d1] = startDate.split('-').map(Number);
    const [y2, m2, d2] = endDate.split('-').map(Number);

    // 30/360 convention adjustments
    let day1 = d1;
    let day2 = d2;

    // If day1 is 31, change it to 30
    if (day1 === 31) day1 = 30;

    // If day2 is 31 and day1 is 30 or 31, change day2 to 30
    if (day2 === 31 && d1 >= 30) day2 = 30;

    // Calculate days
    return (y2 - y1) * 360 + (m2 - m1) * 30 + (day2 - day1);
  }

  /**
   * Calculate the number of complete months between two dates
   * @param startDate Starting date in YYYY-MM-DD format
   * @param endDate Ending date in YYYY-MM-DD format
   * @returns Number of complete months between dates
   */
  static monthsBetween(startDate: string, endDate: string): number {
    if (!isValidDateString(startDate) || !isValidDateString(endDate)) {
      throw new Error('Invalid date strings for monthsBetween calculation');
    }

    const [y1, m1, d1] = startDate.split('-').map(Number);
    const [y2, m2, d2] = endDate.split('-').map(Number);

    let months = (y2 - y1) * 12 + (m2 - m1);

    // Adjust for partial month if end day is before start day
    if (d2 < d1) {
      months--;
    }

    return months;
  }

  /**
   * Calculate the number of years between two dates (including fractional years)
   * @param startDate Starting date in YYYY-MM-DD format
   * @param endDate Ending date in YYYY-MM-DD format
   * @param use360DayYear If true, uses 360-day year convention (default: true for bonds)
   * @returns Number of years between dates (can include decimals)
   */
  static yearsBetween(startDate: string, endDate: string, use360DayYear: boolean = true): number {
    if (!isValidDateString(startDate) || !isValidDateString(endDate)) {
      throw new Error('Invalid date strings for yearsBetween calculation');
    }

    if (use360DayYear) {
      return DateCalculationEngine.days360Between(startDate, endDate) / 360;
    } else {
      return DateCalculationEngine.daysBetween(startDate, endDate) / 365.25;
    }
  }

  /**
   * Calculate the number of whole years between two dates (rounds up)
   * Useful for calculating bond terms
   * @param startDate Starting date in YYYY-MM-DD format
   * @param endDate Ending date in YYYY-MM-DD format
   * @returns Number of whole years (rounded up)
   */
  static wholeYearsBetween(startDate: string, endDate: string): number {
    return Math.ceil(DateCalculationEngine.yearsBetween(startDate, endDate, false));
  }

  /**
   * Get the current year
   * @returns Current year as a number
   */
  static getCurrentYear(): number {
    return new Date().getFullYear();
  }

  /**
   * Check if date1 is before date2
   * @param date1 First date in YYYY-MM-DD format
   * @param date2 Second date in YYYY-MM-DD format
   * @returns true if date1 < date2
   */
  static isDateBefore(date1: string, date2: string): boolean {
    return compareDates(date1, date2) < 0;
  }

  /**
   * Check if date1 is after date2
   * @param date1 First date in YYYY-MM-DD format
   * @param date2 Second date in YYYY-MM-DD format
   * @returns true if date1 > date2
   */
  static isDateAfter(date1: string, date2: string): boolean {
    return compareDates(date1, date2) > 0;
  }

  /**
   * Check if date1 is on or before date2
   * @param date1 First date in YYYY-MM-DD format
   * @param date2 Second date in YYYY-MM-DD format
   * @returns true if date1 <= date2
   */
  static isDateOnOrBefore(date1: string, date2: string): boolean {
    return compareDates(date1, date2) <= 0;
  }

  /**
   * Check if date1 is on or after date2
   * @param date1 First date in YYYY-MM-DD format
   * @param date2 Second date in YYYY-MM-DD format
   * @returns true if date1 >= date2
   */
  static isDateOnOrAfter(date1: string, date2: string): boolean {
    return compareDates(date1, date2) >= 0;
  }

  /**
   * Determine which fiscal year a date belongs to
   * @param date Date in YYYY-MM-DD format
   * @param fiscalYearEndDate Fiscal year end date in YYYY-MM-DD format
   * @returns The fiscal year the date belongs to
   */
  static getFiscalYearForDate(date: string, fiscalYearEndDate: string): number {
    if (!isValidDateString(date) || !isValidDateString(fiscalYearEndDate)) {
      throw new Error('Invalid date strings for fiscal year calculation');
    }

    const dateYear = getYear(date);
    const fiscalEndMonth = parseInt(fiscalYearEndDate.split('-')[1], 10);
    const fiscalEndDay = parseInt(fiscalYearEndDate.split('-')[2], 10);

    // Construct fiscal year end for the same year as the date
    const currentYearFiscalEnd = `${dateYear}-${String(fiscalEndMonth).padStart(2, '0')}-${String(fiscalEndDay).padStart(2, '0')}`;

    // If the date is after the fiscal year end, it belongs to the next fiscal year
    if (DateCalculationEngine.isDateAfter(date, currentYearFiscalEnd)) {
      return dateYear + 1;
    } else {
      return dateYear;
    }
  }

  /**
   * Calculate standard bond periods (typically 6 months)
   * Returns the number of standard periods between two dates
   * @param startDate Starting date in YYYY-MM-DD format
   * @param endDate Ending date in YYYY-MM-DD format
   * @param periodMonths Number of months per period (default: 6 for semi-annual)
   * @returns Number of complete periods
   */
  static periodsBetween(startDate: string, endDate: string, periodMonths: number = 6): number {
    const months = DateCalculationEngine.monthsBetween(startDate, endDate);
    return Math.floor(months / periodMonths);
  }

  /**
   * Get the date of the next payment based on payment frequency
   * @param currentDate Current date in YYYY-MM-DD format
   * @param paymentMonths Array of months when payments occur (1-12)
   * @returns Next payment date in YYYY-MM-DD format
   */
  static getNextPaymentDate(currentDate: string, paymentMonths: number[]): string {
    if (!isValidDateString(currentDate) || paymentMonths.length === 0) {
      throw new Error('Invalid input for next payment date calculation');
    }

    const [year, month, day] = currentDate.split('-').map(Number);

    // Sort payment months
    const sortedMonths = [...paymentMonths].sort((a, b) => a - b);

    // Find next payment month in current year
    for (const paymentMonth of sortedMonths) {
      if (paymentMonth > month || (paymentMonth === month && day < 15)) {
        // Use day 1 as default payment day if not at mid-month
        const paymentDay = day >= 15 ? 15 : 1;
        return `${year}-${String(paymentMonth).padStart(2, '0')}-${String(paymentDay).padStart(2, '0')}`;
      }
    }

    // If no payment months left in current year, use first month of next year
    const nextYear = year + 1;
    const firstMonth = sortedMonths[0];
    return `${nextYear}-${String(firstMonth).padStart(2, '0')}-01`;
  }

  /**
   * Check if a date falls within a specific time window from a reference date
   * Useful for compliance checks like 5-year rules
   * @param date Date to check in YYYY-MM-DD format
   * @param referenceDate Reference date in YYYY-MM-DD format
   * @param windowYears Number of years for the window
   * @returns true if date is within windowYears of referenceDate
   */
  static isWithinYearWindow(date: string, referenceDate: string, windowYears: number): boolean {
    const windowEndDate = addYears(referenceDate, windowYears);
    return (
      DateCalculationEngine.isDateOnOrAfter(date, referenceDate) &&
      DateCalculationEngine.isDateOnOrBefore(date, windowEndDate)
    );
  }

  /**
   * Create a map of years to dates for efficient lookups
   * Useful for timeline projections
   * @param startDate Starting date in YYYY-MM-DD format
   * @param years Number of years to project
   * @param monthsPerYear Array of months to include each year (e.g., [5, 11] for May and Nov)
   * @returns Map of year to array of dates
   */
  static createYearlyDateMap(
    startDate: string,
    years: number,
    monthsPerYear: number[]
  ): Map<number, string[]> {
    const dateMap = new Map<number, string[]>();
    const startYear = getYear(startDate);

    for (let yearOffset = 0; yearOffset < years; yearOffset++) {
      const currentYear = startYear + yearOffset;
      const yearDates: string[] = [];

      for (const month of monthsPerYear) {
        const date = `${currentYear}-${String(month).padStart(2, '0')}-01`;
        if (isValidDateString(date)) {
          yearDates.push(date);
        }
      }

      dateMap.set(currentYear, yearDates);
    }

    return dateMap;
  }
}
